import React, { InputHTMLAttributes, useEffect, useRef, useState } from "react";
import styled from "styled-components";

type TextFieldProps = {
    id?: string;
    tooltip?: string;
    label?: string;
    isEmail?: boolean;
    autoComplete?: string;
} & InputHTMLAttributes<HTMLInputElement>;

const StyledTextFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledLabel = styled.label`
    align-self: stretch;
    color: var(--formant-color-primary-silver);
    font-feature-settings:
        "clig" off,
        "liga" off;
    font-size: 13px;
    font-style: normal;
    line-height: 20px;
    letter-spacing: 0.6px;
`;

const StyledInput = styled.input<{ $isFocused?: boolean }>`
    display: flex;
    height: 40px;
    justify-content: center;
    color: var(--formant-color-primary-white);
    font-size: 13px;
    line-height: 20px;
    letter-spacing: 0.6px;
    background: transparent;
    border: none;
    border-radius: 4px 4px 0px 0px;
    border-bottom: ${({ $isFocused }) =>
        `1px solid ${
            $isFocused ? "white" : " var(--formant-color-primary-steel02)"
        }`};
    border-radius: 4px 4px 0px 0px;

    &::placeholder {
        color: var(--formant-color-primary-silver);
    }
`;

export const TextField: React.FC<TextFieldProps> = ({
    id,
    tooltip,
    label,
    isEmail,
    autoComplete,
    placeholder = label,
    ...rest
}) => {
    const [value, setValue] = useState(rest.value);
    const [isFocused, setIsFocused] = useState(false);
    const textFieldRef = useRef<HTMLInputElement | null>(null);

    const inputType = isEmail ? "email" : "text";

    useEffect(() => {
        const handleFocus = () => {
            setIsFocused(true);
        };

        const handleBlur = () => {
            setIsFocused(false);
        };

        const inputElement = textFieldRef.current;

        if (inputElement) {
            inputElement.addEventListener("focus", handleFocus);
            inputElement.addEventListener("blur", handleBlur);

            return () => {
                inputElement.removeEventListener("focus", handleFocus);
                inputElement.removeEventListener("blur", handleBlur);
            };
        }
    }, []);

    return (
        <StyledTextFieldContainer>
            <StyledLabel htmlFor={id}>{label}</StyledLabel>
            <StyledInput
                ref={textFieldRef}
                $isFocused={isFocused}
                data-tooltip={tooltip}
                id={id}
                type={inputType}
                autoComplete={autoComplete}
                placeholder={placeholder}
                value={value}
                onChange={e => setValue(e.target.value)}
                {...rest}
            />
        </StyledTextFieldContainer>
    );
};
